import useSWR from 'swr';
import {
  ListWorkPlacesResponse,
  GetAWorkplaceResponse,
  ListPayrollsRequestStatus,
  ListPaySchedulesResponse,
  GetPaydaysResponse,
  ListBenefitsResponseItem,
  ListBenefitsResponse,
  ListCompanyBenefitsResponse,
  GetPayrollResponse,
  ListEmployeeTaxDocumentsResponse,
  ListBankAccountsResponseItem,
  ListFilingsResponseResultsItem,
  ListPaymentsResponse,
  ListPayrollsResponseItem,
} from '@thera-hr/check-api-sdk/api/types';
import {
  CheckPayrollResponse,
  GetPayScheduleResponse,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/payroll';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCheckFernClient, useTheraFernClient } from './useFern';
import {
  ICheckBankAccount,
  ICheckCompany,
  ICheckSignatory,
  IGarnishment,
  IListCheckDocuments,
  IListCheckW2Documents,
  ITaxDeposit,
} from 'types/check';
import { useCompany } from 'context/company';
import { globalFetcherSettings } from 'lib/constants';
import useCompanyW2PayrollInfo from './useCompanyW2PayrollInfo';
import { logError } from 'utils/error';
import { extractCursorFromUrl } from 'utils/string';

const refreshOptions = {
  ...globalFetcherSettings,
  refreshInterval: 1000 * 60 * 10,
};

export const useCheckCompanySWR = ({
  checkCompanyId,
  refreshInterval = 1000 * 60 * 10,
}: {
  checkCompanyId: string;
  refreshInterval?: number;
}): {
  checkCompany: ICheckCompany;
  mutateCheckCompanyInfo: () => Promise<ICheckCompany>;
  isPending: boolean;
  error: any;
} => {
  const { checkFernApi, isLoading: isUserLoading } = useCheckFernClient();
  const getCheckCompanyFetcher = async (): Promise<ICheckCompany> => {
    const response = await checkFernApi.getACompany(checkCompanyId);
    return response as unknown as ICheckCompany;
  };
  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(() => (checkCompanyId ? ['getCheckCompanyFetcher', checkCompanyId] : false), getCheckCompanyFetcher, {
    onErrorRetry: (error) => {
      if (error.status === 404) return;
    },
    ...refreshOptions,
    refreshInterval,
  });

  const isLoading = isUserLoading || isSWRLoading;

  return {
    checkCompany: data,
    mutateCheckCompanyInfo: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckSignatoriesSWR = ({
  checkCompanyId,
}: {
  checkCompanyId: string;
}): {
  signatories: ICheckSignatory[];
  mutateSignatories: () => Promise<any>;
  isPending: boolean;
  error: any;
} => {
  const { checkFernApi, isLoading: isFernLoading } = useCheckFernClient();
  const getCheckSignatoriesFetcher = async (): Promise<any> => {
    const response = await checkFernApi.getSignatories(checkCompanyId);
    return response;
  };

  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(
    () => (checkCompanyId ? ['getCheckSignatoriesFetcher', checkCompanyId] : false),
    getCheckSignatoriesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const isLoading = isFernLoading || isSWRLoading;
  const signatories = Array.isArray(data?.results) && data?.results?.length ? data?.results : [];

  return {
    signatories,
    mutateSignatories: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckEnrollmentProfile = ({ checkCompanyId }: { checkCompanyId: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getEnrollmentProfile = async (): Promise<any> => {
    const response = await checkFernApi.getTheEnrollmentProfile(checkCompanyId);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (checkCompanyId && !isCheckFernLoading ? ['getEnrollmentProfile', checkCompanyId] : false),
    getEnrollmentProfile,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const enrollmentProfile = data;

  return {
    enrollmentProfile,
    mutateEnrollmentProfile: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckBankAccountSWR = ({
  bankAccountId,
}: {
  bankAccountId: string;
}): {
  bankAccount: ICheckBankAccount;
  mutateBankAccount: () => Promise<ICheckBankAccount>;
  isPending: boolean;
  error: any;
} => {
  const { checkFernApi, isLoading: isFernLoading } = useCheckFernClient();
  const getCheckBankAccountFetcher = async (): Promise<any> => {
    const response = await checkFernApi.getABankAccount(bankAccountId);
    return response as ICheckBankAccount;
  };

  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(
    () => (bankAccountId ? ['getCheckBankAccountFetcher', bankAccountId] : false),
    getCheckBankAccountFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const isLoading = isFernLoading || isSWRLoading;

  return {
    bankAccount: data,
    mutateBankAccount: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckWorkplaceSWR = ({ workplaceID }: { workplaceID: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAWorkplaceFetcher = async (): Promise<GetAWorkplaceResponse> => {
    const response = await checkFernApi.getAWorkplace(workplaceID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (workplaceID && !isCheckFernLoading ? ['getAWorkplaceFetcher', workplaceID] : false),
    getAWorkplaceFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkWorkplace: data,
    mutateCheckWorkplace: mutate,
    isLoading,
    error,
  };
};

/**
 * This hook is used to get all benefits for a company
 * @param company - The ID of the company to get benefits for
 * @param getTotalResults - When true, fetches all pages of benefits using pagination. Defaults to true.
 * @returns {checkBenefits: ListBenefitsResponseItem[], mutateCheckBenefits: () => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckBenefitByCompanySWR = ({
  company,
  getTotalResults = true,
}: {
  company: string;
  getTotalResults?: boolean;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  // Construct a stable cache key for SWR
  // NOTE: getTotalResults must be included to prevent cache collisions between components
  // with different pagination needs
  const cacheKey =
    company && checkFernApi && !isCheckFernLoading
      ? ['getAllBenefitsFetcher', company, getTotalResults ? 'getTotalResults' : '']
      : null;

  // Fetcher that handles full pagination
  const fetcher = async (): Promise<ListBenefitsResponseItem[]> => {
    return fetchAllBenefits(checkFernApi, {
      company,
      getTotalResults,
    });
  };

  const { data, error, mutate, isLoading } = useSWR(cacheKey, fetcher, {
    onErrorRetry: (error) => {
      if (error.status === 404) return;
    },
    ...refreshOptions,
  });

  return {
    checkBenefits: data || [],
    mutateCheckBenefits: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all benefits for an employee
 * @param employee - The ID of the employee to get benefits for
 * @returns {checkBenefitsOfAnEmployee: ListBenefitsResponse, mutateCheckBenefitsOfAnEmployee: (benefits: ListBenefitsResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckBenefitByEmployeeSWR = ({ employee }: { employee: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllBenefitsFetcher = async (): Promise<ListBenefitsResponse> => {
    const response = await checkFernApi.listBenefits({ employee });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employee && !isCheckFernLoading ? ['getAllBenefitsFetcher', employee] : false),
    getAllBenefitsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const checkBenefitsOfAnEmployee = data?.results || [];

  return {
    checkBenefitsOfAnEmployee,
    mutateCheckBenefitsOfAnEmployee: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all benefits associated with a company
 * @param company - The ID of the company to get benefits for
 * @returns {checkCompanyBenefits: ListCompanyBenefitsResponse, mutateCheckCompanyBenefits: (benefits: ListCompanyBenefitsResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckCompanyBenefitByCompanySWR = ({ company }: { company: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllCompanyBenefitsFetcher = async (): Promise<ListCompanyBenefitsResponse> => {
    const response = await checkFernApi.listCompanyBenefits({ company });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (company && !isCheckFernLoading ? ['getAllCompanyBenefitsFetcher', company] : false),
    getAllCompanyBenefitsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const checkCompanyBenefits = data?.results || [];

  return {
    checkCompanyBenefits,
    mutateCheckCompanyBenefits: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all garnishment for a company
 * @param company - The ID of the company to get garnishment for
 * @returns {checkGarnishmentByCompany: IGarnishment, mutateCheckGarnishmentByCompany: (garnishment: IGarnishment) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckGarnishmentByCompanySWR = ({ company }: { company: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllGarnishmentFetcher = async (): Promise<IGarnishment> => {
    const response = await checkFernApi.listPostTaxDeductions({ company });
    return response as IGarnishment;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (company && !isCheckFernLoading ? ['getAllGarnishmentFetcher', company] : false),
    getAllGarnishmentFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  //@ts-ignore
  const checkGarnishments = data?.results || [];

  return {
    checkGarnishments,
    mutateCheckGarnishments: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all post-tax deductions (miscellaneous deductions and garnishments) for an employee
 * @param employee - The ID of the employee to get post-tax deductions for
 * @returns {checkPostTaxDeductionsOfAnEmployee: IGarnishment[], mutateCheckPostTaxDeductionsOfAnEmployee, isPending, error}
 */
export const useCheckPostTaxDeductionByEmployeeSWR = ({ employee }: { employee: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getEmployeePostTaxDeductionsFetcher = async (): Promise<IGarnishment[]> => {
    const response = await checkFernApi.listPostTaxDeductions({ employee });
    //@ts-ignore - results is not typed on ListPostTaxDeductionsResponse
    return response?.results || [];
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (employee && !isCheckFernLoading ? ['getEmployeePostTaxDeductionsFetcher', employee] : false),
    getEmployeePostTaxDeductionsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkPostTaxDeductionsOfAnEmployee: data || [],
    mutateCheckPostTaxDeductionsOfAnEmployee: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * This hook is used to get all workplaces for a company
 * @param companyID - The ID of the company to get workplaces for
 * @returns {checkWorkplaces: ListWorkPlacesResponse, mutateCheckAllWorkplaces: (workplaces: ListWorkPlacesResponse) => Promise<void>, isPending: boolean, error: Error}
 */
export const useCheckAllWorkplacesSWR = ({ companyID }: { companyID: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllWorkplacesFetcher = async (): Promise<ListWorkPlacesResponse> => {
    const response = await checkFernApi.listWorkplaces({ company: companyID });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getAllWorkplacesFetcher', companyID] : false),
    getAllWorkplacesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    checkWorkplaces: data?.results || [],
    mutateCheckAllWorkplaces: mutate,
    isLoading,
    error,
  };
};

/** Extended payroll type with optional payment information */
export type PayrollWithPayments = ListPayrollsResponseItem & {
  payments?: ListPaymentsResponse['results'];
};

/**
 * Fetches all pages of benefits from the API using cursor-based pagination.
 * Returns results progressively via callback for streaming updates.
 */
async function fetchAllBenefits(
  checkFernApi: ReturnType<typeof useCheckFernClient>['checkFernApi'],
  params: {
    company?: string;
    employee?: string;
    getTotalResults?: boolean;
  },
  onPageFetched?: (benefits: ListBenefitsResponseItem[], isComplete: boolean) => void,
): Promise<ListBenefitsResponseItem[]> {
  const allBenefits: ListBenefitsResponseItem[] = [];
  let cursor: string | undefined;
  // Destructure getTotalResults from params to prevent sending it to the API
  // getTotalResults is an internal flag for controlling pagination behavior
  const { getTotalResults = false, ...apiParams } = params;
  do {
    const response = await checkFernApi.listBenefits({
      ...apiParams,
      cursor,
      limit: 25,
    });

    if (response.results?.length) {
      allBenefits.push(...response.results);
    }

    // Extract cursor from next URL if present
    const nextCursor = response.next && getTotalResults ? extractCursorFromUrl(response.next) : undefined;
    if (nextCursor === cursor && cursor !== undefined) {
      console.warn('fetchAllBenefits: Detected infinite loop with same cursor', cursor);
      break;
    }
    cursor = nextCursor;

    // Notify caller of progress
    onPageFetched?.(allBenefits, !cursor && getTotalResults);
  } while (cursor && getTotalResults);

  return allBenefits;
}

/**
 * Fetches all pages of payrolls from the API using cursor-based pagination.
 * Returns results progressively via callback for streaming updates.
 */
async function fetchAllPayrolls(
  checkFernApi: ReturnType<typeof useCheckFernClient>['checkFernApi'],
  params: {
    company: string;
    status?: ListPayrollsRequestStatus;
    paydayAfter?: string;
    type?: string;
    paySchedule?: string;
    getTotalResults?: boolean;
  },
  onPageFetched?: (payrolls: ListPayrollsResponseItem[], isComplete: boolean) => void,
): Promise<ListPayrollsResponseItem[]> {
  const allPayrolls: ListPayrollsResponseItem[] = [];
  let cursor: string | undefined;
  // Destructure getTotalResults from params to prevent sending it to the API
  // getTotalResults is an internal flag for controlling pagination behavior
  const { getTotalResults = false, ...apiParams } = params;
  do {
    const response = await checkFernApi.listPayrolls({
      ...apiParams,
      cursor,
      limit: 25,
    });

    if (response.results?.length) {
      allPayrolls.push(...response.results);
    }

    // Extract cursor from next URL if present
    const nextCursor = response.next && getTotalResults ? extractCursorFromUrl(response.next) : undefined;

    if (nextCursor === cursor && cursor !== undefined) {
      console.warn('fetchAllPayrolls: Detected infinite loop with same cursor', cursor);
      break;
    }
    cursor = nextCursor;

    // Notify caller of progress
    onPageFetched?.(allPayrolls, !cursor && getTotalResults);
  } while (cursor && getTotalResults);

  return allPayrolls;
}

/**
 * Fetches payment information for a single payroll.
 */
async function fetchPayrollPayments(
  checkFernApi: ReturnType<typeof useCheckFernClient>['checkFernApi'],
  companyId: string,
  payrollId: string,
): Promise<ListPaymentsResponse> {
  return checkFernApi.listPayments({
    company: companyId,
    payroll: payrollId,
    type: 'company_cash_requirement',
  });
}

/**
 * Fetches payment information for multiple payrolls with controlled concurrency.
 * TODO: #123 - to further improve the experience and performance, we should either paginate or stream
 */
async function fetchPaymentsForPayrolls(
  checkFernApi: ReturnType<typeof useCheckFernClient>['checkFernApi'],
  companyId: string,
  payrolls: ListPayrollsResponseItem[],
  concurrency = 5,
): Promise<Map<string, ListPaymentsResponse['results']>> {
  const paymentsMap = new Map<string, ListPaymentsResponse['results']>();

  // Process in batches to avoid overwhelming the API
  for (let i = 0; i < payrolls.length; i += concurrency) {
    const batch = payrolls.slice(i, i + concurrency);
    // NOTE: We intentionally swallow per-payroll payment fetch failures so that
    // a single failed payroll doesn't prevent enriching other payrolls.
    // Failed payroll IDs will simply not exist in the map (payments remain undefined).
    const results = await Promise.all(
      batch.map(async (payroll) => {
        try {
          const response = await fetchPayrollPayments(checkFernApi, companyId, payroll.id);
          return { id: payroll.id, payments: response.results, ok: true as const };
        } catch (err) {
          logError(err, {
            source: 'fetchPaymentsForPayrolls',
            data: {
              companyId,
              payrollId: payroll.id,
            },
            isToastError: false,
          });
          return { id: payroll.id, ok: false as const };
        }
      }),
    );

    results.forEach((result) => {
      if (!result.ok) return;
      paymentsMap.set(result.id, result.payments);
    });
  }

  return paymentsMap;
}

/**
 * Hook to fetch all payrolls for a company with full pagination support.
 * Progressively updates results as pages are fetched, and optionally
 * enriches each payroll with payment information.
 *
 * @param companyID - The Check company ID to fetch payrolls for
 * @param status - Filter payrolls by status
 * @param type - Filter by payroll type ('regular' | 'off-cycle')
 * @param paydayAfter - Return payrolls with payday on or after this date (YYYY-MM-DD)
 * @param payScheduleId - Filter by pay schedule ID
 * @param populatePaymentInfo - When true, fetches and attaches payment details to each payroll
 *
 * @returns Object containing:
 *   - checkPayrolls: Array of payrolls (with payments if populatePaymentInfo is true)
 *   - mutateCheckPayrolls: Function to revalidate the data
 *   - isPending: True while initial data or payments are loading
 *   - error: Any error that occurred during fetching
 */
export const useCheckPayrollSWR = ({
  companyID,
  status,
  paydayAfter,
  type,
  payScheduleId,
  populatePaymentInfo = false,
  getTotalResults = true,
  managed = false,
}: {
  // `companyID` may be temporarily undefined during initial company load.
  // The hook should stay stable (not infinitely pending) in that state.
  companyID?: string;
  status?: ListPayrollsRequestStatus;
  type?: 'regular' | 'off-cycle';
  paydayAfter?: string;
  payScheduleId?: string;
  populatePaymentInfo?: boolean;
  getTotalResults?: boolean;
  managed?: boolean;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();
  const [payrollsWithPayments, setPayrollsWithPayments] = useState<PayrollWithPayments[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const lastFetchedPayrollIdsRef = useRef<string>('');
  // Track the companyID that the current payrollsWithPayments belongs to
  const payrollsCompanyIdRef = useRef<string>('');

  // Construct a stable cache key for SWR
  // NOTE: getTotalResults must be included to prevent cache collisions between components
  // with different pagination needs (e.g., Amplitude uses false, PaymentsTable uses true)
  const cacheKey =
    companyID && checkFernApi && !isCheckFernLoading
      ? [
          'useCheckPayrollSWR',
          companyID,
          status,
          paydayAfter,
          type,
          payScheduleId,
          populatePaymentInfo ? 'populatePaymentInfo' : '',
          getTotalResults ? 'getTotalResults' : '',
        ]
      : null;

  // Fetcher that handles full pagination
  const fetcher = async (): Promise<ListPayrollsResponseItem[]> => {
    return fetchAllPayrolls(checkFernApi, {
      company: companyID,
      status,
      paydayAfter,
      type,
      paySchedule: payScheduleId,
      getTotalResults,
    });
  };

  const {
    data: payrolls,
    error,
    mutate,
    isLoading,
  } = useSWR(cacheKey, fetcher, {
    onErrorRetry: (err) => {
      if (err.status === 404) return;
    },
    ...refreshOptions,
  });

  // Create a stable key from payroll IDs to detect actual data changes
  const payrollIdsKey = useMemo(() => {
    if (!payrolls?.length) return '';
    return payrolls.map((p) => p.id).join(',');
  }, [payrolls]);

  // Reset payment state when companyID changes to prevent showing stale data from previous company
  useEffect(() => {
    // If there's no companyID (e.g., during initial company load), ensure we don't get stuck
    // showing payment-related loading states.
    if (!companyID) {
      setPayrollsWithPayments([]);
      setIsLoadingPayments(false);
      lastFetchedPayrollIdsRef.current = '';
      payrollsCompanyIdRef.current = '';
      return;
    }

    if (companyID !== payrollsCompanyIdRef.current) {
      // Company changed - reset all payment-related state immediately
      setPayrollsWithPayments([]);
      setIsLoadingPayments(populatePaymentInfo);
      lastFetchedPayrollIdsRef.current = '';
      payrollsCompanyIdRef.current = companyID;
    }
  }, [companyID, populatePaymentInfo]);

  // Fetch payments when payrolls are loaded and populatePaymentInfo is true
  useEffect(() => {
    // Early exit conditions - don't fetch payments
    if (!checkFernApi || !populatePaymentInfo || !payrollIdsKey || !companyID) {
      // If populatePaymentInfo is true but there are no payrolls (empty payrollIdsKey),
      // we need to reset isLoadingPayments to false to prevent infinite loading state
      if (checkFernApi && populatePaymentInfo && !payrollIdsKey && companyID) {
        setIsLoadingPayments(false);
      }
      if (!companyID || !populatePaymentInfo) {
        setIsLoadingPayments(false);
      }
      return;
    }

    // If we've already fetched payments for these payroll IDs, we still need to
    // keep payroll fields (status, totals, etc.) in sync when SWR revalidates payrolls.
    // Otherwise, users can see stale payroll data until a full page refresh.
    if (lastFetchedPayrollIdsRef.current === payrollIdsKey) {
      // Only sync if our enriched state belongs to the current company.
      if (payrollsCompanyIdRef.current === companyID && payrolls?.length) {
        setPayrollsWithPayments((prev) => {
          const prevPaymentsById = new Map(prev.map((p) => [p.id, p.payments]));
          return payrolls.map((payroll) => ({
            ...payroll,
            payments: prevPaymentsById.get(payroll.id),
          }));
        });
      }
      return;
    }

    let isCancelled = false;
    setIsLoadingPayments(true);

    (async () => {
      try {
        const paymentsMap = await fetchPaymentsForPayrolls(checkFernApi, companyID, payrolls ?? []);

        if (isCancelled) return;

        const enrichedPayrolls = (payrolls ?? []).map((payroll) => ({
          ...payroll,
          payments: paymentsMap.get(payroll.id),
        }));

        lastFetchedPayrollIdsRef.current = payrollIdsKey;
        setPayrollsWithPayments(enrichedPayrolls);
      } catch (err) {
        logError(err, {
          source: 'useCheckPayrollSWR',
          data: {
            companyID,
            payrollCount: payrolls?.length ?? 0,
            populatePaymentInfo,
          },
          isToastError: false,
        });
        // Payments fetch failed; return payrolls without payment data
        if (!isCancelled) {
          lastFetchedPayrollIdsRef.current = payrollIdsKey;
          setPayrollsWithPayments(payrolls ?? []);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingPayments(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [checkFernApi, companyID, payrollIdsKey, payrolls, populatePaymentInfo]);

  // Check if payrollsWithPayments belongs to the current company
  // This prevents showing stale data during the race window before useEffect runs
  const isPaymentsDataForCurrentCompany = !companyID || payrollsCompanyIdRef.current === companyID;

  // Memoize final payrolls to maintain referential stability and prevent
  // infinite re-render loops in consumers that use checkPayrolls as a useEffect dependency.
  const finalPayrolls = useMemo(() => {
    const base = populatePaymentInfo ? (isPaymentsDataForCurrentCompany ? payrollsWithPayments : []) : (payrolls ?? []);

    if (managed) {
      return base.filter((payroll) => payroll.managed);
    }
    return base;
  }, [populatePaymentInfo, isPaymentsDataForCurrentCompany, payrollsWithPayments, payrolls, managed]);

  // isPending should be true when:
  // 1. Initial API/SWR loading
  // 2. populatePaymentInfo is true AND (payments are loading OR payments data is for a different company)
  const isPending =
    isCheckFernLoading ||
    isLoading ||
    (populatePaymentInfo && Boolean(companyID) && (isLoadingPayments || !isPaymentsDataForCurrentCompany));

  return {
    checkPayrolls: finalPayrolls as PayrollWithPayments[],
    mutateCheckPayrolls: mutate,
    isPending,
    error,
  };
};

export const useCheckPaymentsSWR = ({ payrollID }: { payrollID?: string } = {}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();
  const getPaymentsFetcher = async (): Promise<ListPaymentsResponse> => {
    const response = await checkFernApi.listPayments({ payroll: payrollID });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (payrollID && !isCheckFernLoading ? ['getPaymentsFetcher', payrollID] : false),
    getPaymentsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const payments = Array.isArray(data?.results) && data?.results?.length ? data?.results : [];

  return {
    checkPayments: payments,
    mutateCheckPayments: mutate,
    isPending: isLoading || isCheckFernLoading,
    error,
  };
};

export const useCheckPaySchedulesSWR = ({
  checkCompanyId,
}: {
  checkCompanyId: string;
}): {
  paySchedules: ListPaySchedulesResponse['results'];
  mutatePaySchedules: () => Promise<any>;
  isPending: boolean;
  error: any;
} => {
  const { checkFernApi, isLoading: isFernLoading } = useCheckFernClient();
  const getCheckPaySchedulesFetcher = async (): Promise<any> => {
    const response = await checkFernApi.listPaySchedules({
      company: checkCompanyId,
    });
    return response;
  };

  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(
    () => (checkCompanyId ? ['getCheckPaySchedulesFetcher', checkCompanyId] : false),
    getCheckPaySchedulesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const isLoading = isFernLoading || isSWRLoading;

  const paySchedules =
    Array.isArray(data?.results) && data?.results?.length
      ? data.results.filter((schedule) => schedule.metadata?.deleted?.toLowerCase() !== 'true')
      : [];

  return {
    paySchedules,
    mutatePaySchedules: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckPayDaysSWR = ({
  payScheduleId,
}: {
  payScheduleId: string;
}): {
  paydays: GetPaydaysResponse['results'];
  mutatePaydays: () => Promise<any>;
  isPending: boolean;
  error: any;
} => {
  const { checkFernApi, isLoading: isFernLoading } = useCheckFernClient();
  const getCheckPayDaysFetcher = async (): Promise<any> => {
    const response = await checkFernApi.getPaydays(payScheduleId);
    return response;
  };

  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(() => (payScheduleId ? ['getCheckPayDaysFetcher', payScheduleId] : false), getCheckPayDaysFetcher, {
    onErrorRetry: (error) => {
      if (error.status === 404) return;
    },
    ...refreshOptions,
  });

  const isLoading = isFernLoading || isSWRLoading;
  const paydays = Array.isArray(data?.results) && data?.results?.length ? data?.results : [];

  return {
    paydays,
    mutatePaydays: mutate,
    isPending: isLoading,
    error,
  };
};

export const useW2OnCycleChecksSWR = (): {
  onCycleChecks: any;
  mutateOnCycleChecks: () => Promise<any>;
  isPending: boolean;
  error: any;
} => {
  const { company, isLoading: isCompanyLoading } = useCompany();
  const companyID = company?.companyID;
  const { w2PayrollInfo, isPending: isW2InfoPending } = useCompanyW2PayrollInfo();
  const isPayrollSetup = w2PayrollInfo?.some((payroll) => payroll.theraW2PayrollSetupCompleted);
  const { theraFernApi } = useTheraFernClient();
  const getW2OnCycleChecksFetcher = async (): Promise<any> => {
    const response = await theraFernApi.thera.payroll.oncycleApi.getOnCycleChecks(company?.companyID);
    return response;
  };

  const {
    data,
    error,
    mutate,
    isLoading: isSWRLoading,
  } = useSWR(
    () => (companyID && isPayrollSetup ? ['getW2OnCycleChecksFetcher', companyID] : false),
    getW2OnCycleChecksFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const isLoading = isCompanyLoading || isW2InfoPending || isSWRLoading;

  return {
    onCycleChecks: data,
    mutateOnCycleChecks: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckCurrentOffcyclePayrollSWR = ({ companyID }: { companyID: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getPayrollsFetcher = async (): Promise<CheckPayrollResponse> => {
    const payroll = await theraFernApi.thera.payroll.offcycleApi.getCurrentOffCycleChecks(companyID);
    return payroll;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID ? ['getPayrollsFetcher', companyID] : false),
    getPayrollsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
      keepPreviousData: false,
    },
  );

  return {
    checkPayrolls: data,
    mutateCheckPayrolls: async () => {
      await mutate();
    },
    isPending: isLoading,
    error,
  };
};

export const useCheckCurrentBonusPayrollSWR = ({ companyID }: { companyID: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getBonusPayrollFetcher = async (): Promise<CheckPayrollResponse | null> => {
    const payroll = await theraFernApi.thera.payroll.offcycleApi.getCurrentOffCycleChecks(companyID, {
      payrollType: 'BONUS',
    });
    return payroll ?? null;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID ? ['getCurrentBonusChecks', companyID] : false),
    getBonusPayrollFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
      keepPreviousData: false,
    },
  );

  return {
    checkPayrolls: data,
    mutateCheckPayrolls: async () => {
      await mutate();
    },
    isPending: isLoading,
    error,
  };
};

export const useCheckCurrentOffcyclePayrollWithDetailsSWR = ({ payrollID }: { payrollID: string }) => {
  const { checkFernApi, isLoading: isFernLoading } = useCheckFernClient();

  const getPayrollDetails = async (): Promise<GetPayrollResponse> => {
    const payroll = await checkFernApi.getPayroll(payrollID);
    return payroll;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (payrollID ? ['getPayrollDetails', payrollID] : false),
    getPayrollDetails,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
      keepPreviousData: false,
    },
  );

  return {
    currentPayroll: data,
    mutateCurrentPayroll: mutate,
    isPending: isLoading || isFernLoading,
    error,
  };
};

export const useCheckPayrollDocumentsSWR = ({
  companyID,
  type = 'authorization-documents',
  year,
}: {
  companyID: string;
  type?: 'tax-documents' | 'authorization-documents' | 'setup-documents';
  year?: number;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getPayrollDocumentsFetcher = async (): Promise<IListCheckDocuments> => {
    if (type === 'tax-documents') {
      const response = await checkFernApi.listCompanyTaxDocuments({ company: companyID, year });
      return response as unknown as IListCheckDocuments;
    } else if (type === 'authorization-documents') {
      const response = await checkFernApi.listCompanyAuthorizationDocuments({ company: companyID, year });
      return response as unknown as IListCheckDocuments;
    } else {
      // setup_documents isn't wrapped by @thera-hr/check-api-sdk yet, so hit the raw proxy directly
      const response = await fetch(`/api/check?checkUrl=/documents/setup_documents?company=${companyID}`);
      if (!response.ok) {
        throw new Error(`Failed to load setup documents: ${response.status}`);
      }
      const json = await response.json();
      // the proxy only nests the upstream payload under `data` when checkApi() itself returned an object
      // wrapper; plain list responses (e.g. { results, next }) come back at the top level instead
      return (json?.data ?? json) as IListCheckDocuments;
    }
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getPayrollDocumentsFetcher', companyID, type] : false),
    getPayrollDocumentsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const payrollDocuments = Array.isArray(data?.results) && data?.results?.length ? data?.results : [];

  return {
    checkPayrollDocuments: payrollDocuments,
    mutateCheckPayrollDocuments: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckPayrollW2DocumentsSWR = ({ companyID, year }: { companyID: string; year?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getW2PreviewReportFetcher = async (): Promise<IListCheckW2Documents> => {
    const response = await checkFernApi.getW2PreviewReport(companyID, { year, includeIds: true });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getW2PreviewReportFetcher', companyID] : false),
    getW2PreviewReportFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const w2Documents = Array.isArray(data?.results) && data?.results?.length ? data?.results : [];

  return {
    checkW2Documents: w2Documents,
    mutateCheckW2Documents: mutate,
    isPending: isLoading,
    error,
  };
};

export const useCheckPayrollW4DocumentsSWR = ({ companyID, year }: { companyID: string; year?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getW4PreviewReportFetcher = async (): Promise<ListEmployeeTaxDocumentsResponse> => {
    const response = await checkFernApi.listEmployeeTaxDocuments({ company: companyID, year: Number(year) });
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getW4PreviewReportFetcher', companyID] : false),
    getW4PreviewReportFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );
  const w4Documents =
    Array.isArray(data?.results) && data?.results?.length
      ? data.results.filter((doc) => !doc.label?.includes('W-2'))
      : [];

  return {
    checkW4Documents: w4Documents,
    mutateCheckW4Documents: mutate,
    isPending: isLoading,
    error,
  };
};

// pay cycles
export const useGetPaySchedules = () => {
  const { company, isLoading: isCompanyLoading } = useCompany();
  const companyId = company?.companyID;
  const checkCompanyId = company?.w2Payroll?.checkId;
  const { theraFernApi } = useTheraFernClient();

  const getPaySchedulesFetcher = async (): Promise<GetPayScheduleResponse[]> => {
    const response = await theraFernApi.thera.payroll.payscheduleApi.listW2PaySchedules(companyId);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyId && !isCompanyLoading && checkCompanyId ? ['getPaySchedulesFetcher', companyId] : false),
    getPaySchedulesFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    w2PaySchedules: Array.isArray(data) && data?.length > 0 ? data : [],
    mutateW2PaySchedules: mutate,
    isPending: isCompanyLoading || isLoading,
    error,
  };
};

export const useGetPaySchedule = ({ payScheduleId }: { payScheduleId: string }) => {
  const { theraFernApi } = useTheraFernClient();
  const { company, isLoading: isCompanyLoading } = useCompany();
  const companyId = company?.companyID;

  const getPayScheduleFetcher = async (): Promise<GetPayScheduleResponse> => {
    const response = await theraFernApi.thera.payroll.payscheduleApi.getW2PaySchedule(companyId, payScheduleId);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyId && !isCompanyLoading ? ['getPayScheduleFetcher', companyId, payScheduleId] : false),
    getPayScheduleFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    paySchedule: data,
    mutatePaySchedule: mutate,
    isPending: isCompanyLoading || isLoading,
    error,
  };
};

export const useCheckPayrollTaxDepositsSWR = ({
  companyID,
  payrollYear,
}: {
  companyID: string;
  payrollYear?: string;
}) => {
  const payrollPaydayStart = payrollYear ? `${payrollYear}-01-01` : undefined;
  const payrollPaydayEnd = payrollYear ? `${payrollYear}-12-31` : undefined;

  const getAllTaxDepositsFetcher = async (): Promise<ITaxDeposit[]> => {
    const allTaxDeposits: ITaxDeposit[] = [];
    let nextUrl: string | null = null;

    do {
      let url: string;
      if (!nextUrl) {
        const params = new URLSearchParams();
        if (payrollPaydayStart) {
          params.append('payroll_payday_start', payrollPaydayStart);
        }
        if (payrollPaydayEnd) {
          params.append('payroll_payday_end', payrollPaydayEnd);
        }
        url = `/api/check?checkUrl=/companies/${companyID}/tax_deposits&${params.toString()}`;
      } else {
        const urlObj = new URL(nextUrl);
        url = `/api/check?checkUrl=${urlObj.pathname}${urlObj.search}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tax deposits: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (Array.isArray(data.results)) {
        allTaxDeposits.push(...data.results);
      }

      nextUrl = data.next;
    } while (nextUrl !== null);

    return allTaxDeposits;
  };

  const { data, error, mutate, isLoading, isValidating } = useSWR(
    () => (companyID ? ['getAllTaxDepositsFetcher', companyID, payrollYear] : false),
    getAllTaxDepositsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const taxDeposits = Array.isArray(data) && data?.length ? data : [];

  return {
    checkTaxDeposits: taxDeposits,
    mutateCheckTaxDeposits: mutate,
    isPending: isLoading || isValidating,
    error,
  };
};

export const useCheckTaxFilingsSWR = ({ companyID, year }: { companyID: string; year?: string }) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getAllTaxFilingsFetcher = async (): Promise<ListFilingsResponseResultsItem[]> => {
    const allFilings: ListFilingsResponseResultsItem[] = [];
    let cursor: string | undefined;

    do {
      const response = await checkFernApi.listFilings({
        company: companyID,
        year: year ? Number(year) : undefined,
        cursor,
        limit: 100,
      });

      if (response.results?.length) {
        allFilings.push(...response.results);
      }

      const nextCursor = response.next ? extractCursorFromUrl(response.next) : undefined;
      if (nextCursor === cursor && cursor !== undefined) {
        console.warn('getAllTaxFilingsFetcher: Detected infinite loop with same cursor', cursor);
        break;
      }
      cursor = nextCursor;
    } while (cursor);

    return allFilings;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (companyID && !isCheckFernLoading ? ['getAllTaxFilingsFetcher', companyID, year] : false),
    getAllTaxFilingsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  const taxFilings = Array.isArray(data) && data?.length ? data : [];

  return {
    checkTaxFilings: taxFilings,
    mutateCheckTaxFilings: mutate,
    isPending: isCheckFernLoading || isLoading,
    error,
  };
};

export const useCheckCompanyBankAccounts = () => {
  const { company, isLoading: isCompanyLoading } = useCompany();
  const companyId = company?.companyID;
  const checkCompanyId = company?.w2Payroll?.checkId;
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const getBankAccountsFetcher = async (): Promise<ListBankAccountsResponseItem[]> => {
    const response = await checkFernApi.listBankAccounts({ company: checkCompanyId });
    return response?.results ?? [];
  };

  const { data, error, mutate, isLoading } = useSWR(
    () =>
      companyId && !isCompanyLoading && checkCompanyId ? ['getBankAccountsFetcher', companyId, checkCompanyId] : false,
    getBankAccountsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...refreshOptions,
    },
  );

  return {
    bankAccounts: Array.isArray(data) && data?.length > 0 ? data : [],
    mutateBankAccounts: mutate,
    isPending: isCompanyLoading || isCheckFernLoading || isLoading,
    error,
  };
};

// NY params whose labels match these strings are never genuinely actionable:
// - "MCTMT" (Metropolitan Commuter Transportation Mobility Tax): optional metro tax, companies not in the district can ignore it // cspell:ignore MCTMT
// - PIN: applied_for=true when not applicable, but we exclude by label to be safe
// - reimbursable employer: only applies to 501(c)(3) non-profits; Check returns setting=null for other company types
const NY_EXCLUDED_PARAM_LABELS = [
  'MCTMT', // cspell:ignore MCTMT
  'PIN',
  'reimbursable employer',
];

const isNYParamActionable = (param: {
  label?: string;
  setting?: { value?: unknown; applied_for?: boolean } | null;
}) => {
  // Filter by label (human-readable field), NOT name (snake_case identifier like new_york_sui_reimbursable_election)
  if (NY_EXCLUDED_PARAM_LABELS.some((excluded) => param.label?.includes(excluded))) return false;
  // setting===null means the param is conditional (depends_on) and doesn't apply to this company type
  if (param.setting === null) return false;
  // Actionable = no value set yet
  // eslint-disable-next-line eqeqeq
  return param.setting?.value == null;
};

/**
 * Fetches all NY tax params in a single call (?jurisdiction=nyJurisdictionId returns all NY params).
 * Only fires when NY is in setupParameters with needs_attention — if NY is blocking we leave it
 * visible as-is. Used by useTaxSetupIncomplete to suppress the NY pending action item when all
 * remaining params are non-actionable (MCTMT/PIN/reimbursable or already have values set).
 */
export const useNYTaxParamsSWR = ({
  checkCompanyId,
  nyJurisdictionId,
}: {
  checkCompanyId: string;
  nyJurisdictionId: string | null;
}) => {
  const { checkFernApi, isLoading: isCheckFernLoading } = useCheckFernClient();

  const fetcher = async (): Promise<{
    results: { label?: string; setting?: { value?: unknown; applied_for?: boolean } | null }[];
  }> => {
    return await checkFernApi.listCompanyTaxParameters(checkCompanyId, { jurisdiction: nyJurisdictionId });
  };

  const { data, isLoading } = useSWR(
    () =>
      checkCompanyId && nyJurisdictionId && !isCheckFernLoading
        ? ['useNYTaxParamsSWR', checkCompanyId, nyJurisdictionId]
        : false,
    fetcher,
    refreshOptions,
  );

  // Default true while loading — avoids incorrectly hiding NY before we know the answer
  const hasActionableNYParams = data?.results?.some(isNYParamActionable) ?? true;

  return { hasActionableNYParams, isLoading: isCheckFernLoading || isLoading };
};
